import { type InputHTMLAttributes, forwardRef, useEffect, useRef, useState } from "react";

type BufferedTextInputProps = {
  value: string;
  onCommit: (value: string) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange">;

export const BufferedTextInput = forwardRef<HTMLInputElement, BufferedTextInputProps>(function BufferedTextInput(
  { value, onCommit, onBlur, onFocus, onKeyDown, ...rest },
  ref,
) {
  const [draftValue, setDraftValue] = useState(value);
  const isFocusedRef = useRef(false);
  const valueAtFocusRef = useRef(value);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setDraftValue(value);
    }
  }, [value]);

  const commit = () => {
    if (draftValue !== valueAtFocusRef.current) {
      onCommit(draftValue);
    }
  };

  return (
    <input
      {...rest}
      ref={ref}
      value={draftValue}
      onFocus={(event) => {
        isFocusedRef.current = true;
        valueAtFocusRef.current = value;
        onFocus?.(event);
      }}
      onChange={(event) => {
        setDraftValue(event.target.value);
      }}
      onBlur={(event) => {
        isFocusedRef.current = false;
        commit();
        onBlur?.(event);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          (event.currentTarget as HTMLInputElement).blur();
        }
        onKeyDown?.(event);
      }}
    />
  );
});
